import { Dialog, DialogContent, DialogTitle, Stack } from "@mui/material";
import dayjs from "dayjs";
import React from "react";

import { EditableTableSkeleton } from "components/common/plan/EditableTableSkeleton";

import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";

import { PlanId } from "components/common/boundary/PlanId";
import { BackupTable } from "components/common/plan/BackupTable";
import { EditableTableActions } from "components/common/plan/EditableTableActions";
import { Period } from "hooks/periodIndex";
import {
  ChildMixUpdate,
  DeployedPlannedResultsRead,
  generatedApi,
  MaterialRead,
  NameBasedPeriodMaterialSummary,
  ProductionMixResultRead,
  SteelGrade,
  useGetDeployedPlanResultsQuery,
  useGetMaterialMetadataSetQuery,
  useUpdateAndDeployPlanResultsMutation,
} from "src/store/api/generatedApi";
import {
  selectSearchedSteelGradeIds,
  steelGradeSearchSlice,
} from "src/store/slices/steelGradeSearchSlice";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { typeSafeObjectFromEntries } from "src/utils/typeSafeObjectFromEntries";

interface Props {
  open: boolean;
  doClose: () => void;
  period: Period;
}

interface PlanContextWrapperProps {
  period: Period;
  deployedPlanResults: DeployedPlannedResultsRead;
  materials: MaterialRead[];
}

interface BodyProps {
  planId: PlanId;
  results: ProductionMixResultRead[];
  materials: MaterialRead[];
  periodMaterialSummaries: NameBasedPeriodMaterialSummary[];
  steelGrades: SteelGrade[];
  startAt: Date;
  period: Period;
}

const useHandleOnSubmit = (planId: PlanId, period: Period) => {
  const dispatch = useAppDispatch();
  const tenantName = useTenant();
  const [doUpdateAndDeployPlanResults] =
    useUpdateAndDeployPlanResultsMutation();

  const doSubmit = React.useCallback(
    async (updates: ChildMixUpdate[]) => {
      const startAt = new Date();
      const updatedDeployedPlanResults = await doUpdateAndDeployPlanResults({
        tenantName,
        planId,
        deployedPlanMixUpdates: {
          period_updates: [
            {
              updates,
              replacements: {},
              period,
            },
          ],
          layer_source: "per_basket_totals",
          start_at: startAt.toISOString(),
          end_at: dayjs(startAt).add(1, "year").toISOString(),
          deployment_type: "backup",
        },
      });
      if (updatedDeployedPlanResults.data) {
        dispatch(
          generatedApi.util.updateQueryData(
            "getDeployedPlanResults",
            {
              tenantName,
              deploymentTypes: ["backup", "edited_backup"],
              planId: "latest",
              period,
            },
            () => updatedDeployedPlanResults.data
          )
        );
        dispatch(
          generatedApi.util.updateQueryData(
            "getDeployedPlans",
            {
              tenantName,
              deploymentTypes: ["backup"],
            },
            (draft) => {
              return {
                ...draft,
                latest: updatedDeployedPlanResults.data.plan_id,
              };
            }
          )
        );
        dispatch(
          steelGradeSearchSlice.actions.copy([
            planId,
            period,
            "backup",
            updatedDeployedPlanResults.data.plan_id as PlanId,
            period,
          ])
        );
      }
    },
    [doUpdateAndDeployPlanResults, dispatch, tenantName, planId, period]
  );
  return doSubmit;
};

export const Body = ({
  planId,
  results,
  materials,
  startAt,
  period,
  periodMaterialSummaries,
  steelGrades,
}: BodyProps) => {
  const { t } = useTenantTranslation();
  const handleOnSubmit = useHandleOnSubmit(planId, period);

  const inventoryByMaterialName = typeSafeObjectFromEntries(
    periodMaterialSummaries
      .filter((summary) => summary.period === period)
      .map((summary) => [
        summary.material,
        summary.initial_inventory + summary.obtainable_obtained,
      ])
  );

  const filteredSteelGrades = React.useMemo(() => {
    const steelGradeNames = new Set(
      results.flatMap((result) =>
        result.production.map((item) => item.steel_grade_name)
      )
    );
    return steelGrades.filter((steelGrade) =>
      steelGradeNames.has(steelGrade.name)
    );
  }, [results, steelGrades]);

  const hasDri = materials.some(
    (material) => material.addition_location === "hopper"
  );
  const tableMaterials = React.useMemo(() => {
    return materials
      .filter((material) => material.addition_location === "basket")
      .sort(
        (materialA, materialB) =>
          materialA.deployment_ordering - materialB.deployment_ordering
      )
      .map((material) => ({
        id: material.id,
        name: material.name,
        inventory: inventoryByMaterialName[material.name] ?? null,
      }));
  }, [materials, inventoryByMaterialName]);

  const selectedSteelGradeIds = useAppSelector((state) =>
    selectSearchedSteelGradeIds(state, planId, period, "backup")
  );

  const timeString = `${t("lastDeployedOn")} ${dayjs(startAt).format(
    "MMM D YYYY HH:mm"
  )}`;

  return (
    <DialogContent sx={{ display: "flex", flexDirection: "column" }}>
      <Stack sx={{ minHeight: 0, display: "flex" }}>
        <EditableTableActions
          timeString={timeString}
          view="backup"
          planId={planId}
          period={period}
          materials={materials}
          steelGrades={filteredSteelGrades}
          showDriConsumption={false}
          onSubmit={handleOnSubmit}
          results={results}
        >
          {(onChange, setMixDescription, isSubmitting, hasEdited, rows) => (
            <BackupTable
              planId={planId}
              onChange={(rows) => {
                onChange(planId, period, rows);
              }}
              disableEdit={isSubmitting}
              hasEdited={hasEdited}
              rows={rows}
              materials={tableMaterials}
              hasDri={hasDri}
              selectedSteelGradeIds={selectedSteelGradeIds}
              setMixDescription={setMixDescription}
            />
          )}
        </EditableTableActions>
      </Stack>
    </DialogContent>
  );
};

const PlanContextWrapper = ({
  deployedPlanResults,
  period,
  materials,
}: PlanContextWrapperProps) => {
  const {
    plan_id: planId,
    periods,
    start_at,
    summary,
    steel_grade_set,
  } = deployedPlanResults;

  const startAt = React.useMemo(() => new Date(start_at), [start_at]);
  const periodResults = periods[period.toString()]?.mixes ?? [];

  return (
    <Body
      planId={planId as PlanId}
      results={periodResults}
      materials={materials}
      startAt={startAt}
      period={period}
      periodMaterialSummaries={summary.period_material_summary}
      steelGrades={steel_grade_set.steel_grades}
    />
  );
};

export const EditBackupPlanModal = ({ open, doClose, period }: Props) => {
  const tenantName = useTenant();
  const { t } = useTenantTranslation();
  const deployedPlanResults = useGetDeployedPlanResultsQuery(
    {
      tenantName,
      deploymentTypes: ["backup", "edited_backup"],
      planId: "latest",
      period,
    },
    { skip: !open }
  );
  // Shadow query for migration diff coverage (Meticulous). Result unused.
  useGetDeployedPlanResultsQuery(
    {
      tenantName,
      deploymentTypes: ["backup", "edited_backup"],
      planId: "latest",
      period,
    },
    { skip: !open }
  );
  const { data: materialMetadataSet } = useGetMaterialMetadataSetQuery({
    tenantName,
    materialMetadataSetId: "default",
  });
  return (
    <Dialog
      open={open}
      onClose={doClose}
      fullWidth
      maxWidth="xl"
      sx={{
        "& .MuiDialog-paper": {
          height: "90vh",
        },
      }}
    >
      <DialogTitle>{t("editBackupPlan")}</DialogTitle>

      {deployedPlanResults.isSuccess && materialMetadataSet ? (
        <PlanContextWrapper
          deployedPlanResults={deployedPlanResults.data}
          period={period}
          materials={materialMetadataSet.materials}
        />
      ) : (
        <EditableTableSkeleton />
      )}
    </Dialog>
  );
};
